import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@mui/material";
import MxFormL3AutoComplete from "@rf-l3/form/MxForml3AutoComplete";
import { A_REALESTATE_TYPES } from "@/src-refactor/modules/shared/model/const/property/real-estate-type.const";
import { A_PROPERTIES_STATUS_ARRAY } from "@/src-refactor/modules/shared/model/const/property/real-estate-status";
import MxButton2 from "@rf-l1/buttons/MxButton2";
import MxTypography from "@rf-l1/MxTypography";
import MxFormL3CityField from "@rf-l3/form/MxFormL3CityField";
import MxFormL3SelectSectorField from "@rf-l3/form/MxForml3Sectors";
import MxFormL3RushSwitchField from "@rf-l3/form/MxFormL3RushSwitchField";
import { A_CURRENCY } from "@/src-refactor/modules/shared/model/const/currency.conts";
import useModal from "@rf/base/modal/useModal";
import MxModalTemplate2 from "@rf-l1/modal/MxModalTemplate2";
import { A_BUSINESS_DETAILS } from "@/src-refactor/modules/shared/model/const/property/bussines-detail";
import MxFormL3SelectAgentsField from "@rf-l3/form/MxFormL3AgentsField";
import MxFormL3SelectAgenciesField from "@rf-l3/form/MxFormL3AgeciesField";
import MxFormL3RushCounterField from "@rf-l3/form/MxFormL3Counter";
import MxFormL3CheckBoxField from "@rf-l3/form/MxFormL3CheckBoxField";
import {
  CPropertyListFilter,
  CPropertyListFilterKeys,
  IPropertyListFilterSchema,
  PropertyListFilterSchema
} from "../util/propertyListFilter";
import {
  A_BUSINESS_TYPE,
  C_BUSINESS_TYPE
} from "@/src-refactor/modules/shared/model/const/property/business-type.const";
import { useCallback, useMemo } from "react";
import { memo } from "react";
import HCheckArray from "@rf-helper/check-have-value";
import MxFormL3TextThousandField from "@rf-l3/form/MxFormL3TextThousandField";
import MxFormL3ImprovementsField from "@rf-l3/form/MxFormL3ImprovementsField";
import MxFormL3SelectTrustField from "@rf-l3/form/MxFormL3SelectTrustField";
import MxFormL3ConditionStatusPropertyField from "@rf-l3/form/MxFormL3ConditionStatusPropertyField";
import MxFormL3TextDateField from "@rf-l3/form/MxFormL3DatePickerField";

const usePropertyListFilterModal = () => {
  const { modal } = useModal();
  const { getValues, reset } = useFormContext();

  const handleOpenModal = () => {
    modal({
      template: <PropertyFiltersTemplate />,
      data: getValues(),
      options: {
        title: "Filtros",
        size: {
          xs: {
            width: "100%",
            height: "100%"
          },
          sm: {
            width: "100%",
            height: "100%"
          },
          md: {
            width: "100%"
          },
          lg: {
            width: "700px"
          },
          xl: {
            width: "700px"
          }
        }
      }
    }).then(data => {
      if (data.type.confirm) {
        reset(data.value as IPropertyListFilterSchema);
      }
    });
  };

  return { handleOpenModal };
};

const PropertyFiltersTemplate = () => {
  const { onConfirm, getData } = useModal();
  const formExternal = getData() as IPropertyListFilterSchema;

  const form = useForm({
    defaultValues: {
      ...CPropertyListFilter,
      ...formExternal
    },
    resolver: yupResolver(PropertyListFilterSchema)
  });

  const businessTypeSelection = useWatch({
    control: form.control,
    name: CPropertyListFilterKeys.businessType
  });

  const isRent = useMemo(() => {
    if (HCheckArray(businessTypeSelection) && businessTypeSelection.length == 1) {
      return businessTypeSelection[0].id == C_BUSINESS_TYPE.SELL.id;
    } else {
      return false;
    }
  }, [businessTypeSelection]);

  const handleSubmit = (data: IPropertyListFilterSchema) => {
    onConfirm(data);
  };

  const handleClear = () => {
    form.reset(CPropertyListFilter);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <MxModalTemplate2
          children={
            <Grid container columnSpacing={1} rowSpacing={2.5}>
              <Grid item xs={12} md={6}>
                <MxFormL3AutoComplete
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.type}
                  label="Tipo de propiedad"
                  items={A_REALESTATE_TYPES}
                  isMultiple={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MxFormL3AutoComplete
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.businessType}
                  label="Tipo de negocio"
                  items={A_BUSINESS_TYPE}
                  isMultiple={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MxFormL3AutoComplete
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.detailType}
                  label="Detalle de negocio"
                  items={A_BUSINESS_DETAILS}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MxFormL3AutoComplete
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.status}
                  label="Estado"
                  items={A_PROPERTIES_STATUS_ARRAY}
                  isMultiple={true}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <MxFormL3RushSwitchField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.currencySale}
                  items={A_CURRENCY}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MxFormL3TextThousandField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.priceMin}
                  label="Precio mínimo"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <MxFormL3TextThousandField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.priceMax}
                  label="Precio máximo"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <MxFormL3RushSwitchField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.maintenanceCurrency}
                  items={A_CURRENCY}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MxFormL3TextThousandField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.maintenanceMin}
                  label="Precio de mantenimiento mínimo"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <MxFormL3TextThousandField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.maintenanceMax}
                  label="Precio de mantenimiento máximo"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MxFormL3TextThousandField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.metersLandMin}
                  label="Metros de solar mínimo"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MxFormL3TextThousandField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.metersLandMax}
                  label="Metros de solar máximo"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MxFormL3TextThousandField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.metersConstructionMin}
                  label="Metros de construcción mínimo"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MxFormL3TextThousandField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.metersConstructionMax}
                  label="Metros de construcción máximo"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MxFormL3ConditionStatusPropertyField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.condition}
                  label="Condición de la propiedad"
                  isRent={isRent}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MxFormL3TextThousandField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.percentCommission}
                  label="Porcentaje de comisión"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MxFormL3SelectTrustField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.fideicomiso}
                  label="Fideicomiso"
                />
              </Grid>

              <CityAndSectorFormSection />

              <Grid item xs={12} md={6}>
                <MxFormL3SelectAgentsField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.agents}
                  label="Agentes"
                  isMultiple={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MxFormL3SelectAgenciesField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.agencies}
                  label="Agencias"
                  isMultiple={true}
                />
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <MxFormL3TextDateField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.createdAt}
                  label="Fecha de creación"
                  disableFuture
                />
              </Grid> */}

              <Grid item xs={12}>
                <MxTypography variant="caption" color="grey.700">
                  Seleccione una o mas mejoras
                </MxTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MxFormL3ImprovementsField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.improvements}
                  label="Mejoras"
                  isMultiple={true}
                />
              </Grid>
              <Grid item xs={12}>
                <MxFormL3RushCounterField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.bedroom}
                  label="Habitaciones"
                  multiple
                  countRender={7}
                />
              </Grid>
              <Grid item xs={12}>
                <MxFormL3RushCounterField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.bathroom}
                  label="Baños"
                  multiple
                  countRender={7}
                />
              </Grid>
              <Grid item xs={12}>
                <MxFormL3RushCounterField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.halfBathroom}
                  label="Medios baños"
                  multiple
                  countRender={7}
                />
              </Grid>
              <Grid item xs={12}>
                <MxFormL3RushCounterField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.parking}
                  label="Parqueos"
                  multiple
                  countRender={7}
                />
              </Grid>
              <Grid item xs={12}>
                <MxFormL3RushCounterField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.levelFloor}
                  label="Nivel de piso"
                  multiple
                  countRender={7}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <MxFormL3CheckBoxField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.myProperties}
                  label="Mis propiedades"
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <MxFormL3CheckBoxField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.masterBroker}
                  label="Master broker"
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <MxFormL3CheckBoxField
                  withreacthookform={1}
                  name={CPropertyListFilterKeys.isCollection}
                  label="The RE/MAX Collection"
                  positionlabel="right"
                />
              </Grid>

              <Grid container item xs={12} justifyContent="center" gap={1}>
                <MxButton2
                  children="Aplicar filtros"
                  variant="contained"
                  bgColor="primary.main"
                  type="submit"
                  testId="apply-filters-button"
                />
                <MxButton2
                  children="Limpiar filtros"
                  variant="text"
                  textColor="error.main"
                  onClick={handleClear}
                  testId="clear-filters-button"
                />
              </Grid>
            </Grid>
          }
        />
      </form>
    </FormProvider>
  );
};

export default usePropertyListFilterModal;

const CityAndSectorFormSection = memo(() => {
  const { setValue, control } = useFormContext();

  const CityField = useWatch({
    name: CPropertyListFilterKeys.city,
    defaultValue: [],
    control
  });

  const handleUpdateCity = useCallback(() => {
    setValue(CPropertyListFilterKeys.sector, []);
  }, []);

  const CityId = useMemo(() => {
    return HCheckArray(CityField) ? CityField[0].value : undefined;
  }, [CityField]);

  return (
    <>
      <Grid item xs={12} md={6}>
        <MxFormL3CityField
          name={CPropertyListFilterKeys.city}
          label="Ciudad"
          withreacthookform={1}
          onSelect={handleUpdateCity}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <MxFormL3SelectSectorField
          name={CPropertyListFilterKeys.sector}
          label="Sector"
          cityId={CityId}
          withreacthookform={1}
          isMultiple={true}
          disabled={!CityId}
        />
      </Grid>
    </>
  );
});
